// ── HandHome — главная темы «Колода» (layout "hand", по макету пользователя из Figma) ───
// Приветствие «Твой ход, Антон.» — HandGreeting (Home.js ставит его первым, над объявлениями).
// Большая панель DAILY HAND — три карты из
// сегодняшнего повторения веером (кремовые, с достоинством и мастью в углах, слово крупно,
// перевод подписью), золотая монета — сколько карт на сегодня; нажатие — начать повторение.
// Ниже — «Продолжить» (последнее аудио), «Библиотека» плитками в две колонки с «+ Новый»,
// справа — слоты, внизу — подвал. Сводка дня (повторить, план, стрик) — в боковом меню
// (DeckWidgets); на телефоне меню нет — она здесь же, вверху.
//
// Данные и действия приходят из ProjectsScreen (Home.js): он же строит плитки библиотеки.

import { useMemo } from 'react';
import DeckWidgets from '../components/DeckWidgets';
import WordSlots from '../components/WordSlots';
import { t, uiLocale } from '../i18n';
import Icon from '../Icon';
import { SANS } from '../themes';
import { Button, IconButton } from '../ui';
import { panelSurface } from '../ui/surface';
import { fmtClock } from '../playPos';
import { ruPlural } from '../utils';

const SUITS = [['A', '♠', '#1d2b2c'], ['6', '♥', '#c43b35'], ['2', '♣', '#1d2b2c']];
const units = (w) => [...String(w || '')].reduce((n, ch) => n + (/[⺀-꓏가-힣＀-￯]/.test(ch) ? 2 : 1), 0);
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Три карты на сегодня: сначала к повторению (срок наступил), потом новые.
function todayHand(cards) {
  const now = Date.now();
  const due = [];
  const fresh = [];
  for (const c of cards) {
    if (!c.word) continue;
    const st = c.srs_state || 'new';
    if (st === 'new') fresh.push(c);
    else if (!c.srs_due || new Date(c.srs_due).getTime() <= now) due.push(c);
  }
  return [...due, ...fresh].slice(0, 3);
}

// k — масштаб (на телефоне карты меньше, чтобы веер помещался в панель).
function HandCard({ C, card, i, n, k = 1 }) {
  const [rank, suit, sc] = SUITS[i % SUITS.length];
  const F = C.face;
  const u = units(card.word);
  const size = (u <= 2 ? 48 : u <= 4 ? 36 : u <= 8 ? 24 : 17) * k;
  const mid = (n - 1) / 2;
  return (
    <div data-playcard="" style={{ position: 'relative', width: 122 * k, height: 156 * k, flexShrink: 0, marginLeft: i ? -14 * k : 0, boxSizing: 'border-box',
      background: F.bg, border: `2px solid ${F.border}`, borderRadius: 8, boxShadow: '0 10px 18px -8px rgba(0,0,0,.55), 0 2px 0 rgba(0,0,0,.25)',
      rotate: `${(i - mid) * 6}deg`, translate: `0 ${Math.abs(i - mid) * 8 * k}px`, color: F.text, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', padding: `${22 * k}px 6px ${26 * k}px` }}>
      <span aria-hidden="true" data-display="" style={{ position: 'absolute', top: 6, left: 8, fontSize: 13, color: sc }}>{rank}</span>
      <span aria-hidden="true" style={{ position: 'absolute', top: 5, right: 8, fontSize: 14, color: sc }}>{suit}</span>
      <span data-serif="" style={{ fontSize: size, lineHeight: 1.1, fontWeight: 600, textAlign: 'center', overflowWrap: 'anywhere', maxWidth: '100%', color: sc }}>{card.word}</span>
      <span style={{ position: 'absolute', bottom: 8, left: 6, right: 6, textAlign: 'center', fontSize: k < 1 ? 9 : 9.5, fontWeight: 700, letterSpacing: '.08em',
        textTransform: 'uppercase', color: F.mid, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{card.translation}</span>
    </div>
  );
}

// «Твой ход, Антон.» — первым на странице, выше объявлений (их Home.js ставит под ним).
export function HandGreeting({ C, navMode, userName, email, activeLang }) {
  const name = cap(userName || (email || '').split('@')[0] || '');
  let langName = '';
  try { langName = activeLang ? cap(new Intl.DisplayNames([uiLocale()], { type: 'language' }).of(activeLang)) : ''; } catch { langName = ''; }
  const gold = C.gold || C.warm;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', fontFamily: SANS }}>
      <div style={{ minWidth: 0 }}>
        <h1 style={{ margin: 0, fontSize: navMode ? 24 : 30, fontWeight: 700, color: C.text, letterSpacing: '-0.015em' }}>
          {name ? t("Твой ход, {0}.", [name]) : t("Твой ход.")}
        </h1>
        <div style={{ fontSize: 13, color: C.faint, marginTop: 4 }}>
          {langName ? t("{0} каждый день. По одной хорошей руке.", [langName]) : t("Каждый день. По одной хорошей руке.")}
        </div>
      </div>
      <span data-display="" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: gold, background: C.surface,
        border: `1px solid ${C.border}`, borderRadius: 4, padding: '5px 9px', whiteSpace: 'nowrap' }}>
        <span aria-hidden="true" style={{ width: 6, height: 6, background: gold }} />READY TO LEARN
      </span>
    </div>
  );
}

export default function HandHome({
  C, navMode, cards, plan, last, playingId, onOpen, onPlayHere, onQuiz, onNewMenu, chips, library, slotWords,
}) {
  const hand = useMemo(() => todayHand(cards || []), [cards]);
  const panel = { ...panelSurface(C), borderRadius: 10, boxSizing: 'border-box' };
  const gold = C.gold || C.warm;
  const total = plan.total;

  const hero = (
    <div data-panel="" role="button" tabIndex={0} className="ac-row-click" onClick={() => total && onQuiz(null, true)}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && total) { e.preventDefault(); onQuiz(null, true); } }}
      aria-label={t("Начать повторение")} style={{ ...panel, padding: navMode ? '18px 16px' : '24px 24px', display: 'flex', alignItems: 'center',
        gap: 16, flexWrap: 'wrap', cursor: total ? 'pointer' : 'default', minHeight: navMode ? 0 : 200 }}>
      <div style={{ flex: '1 1 240px', minWidth: 0 }}>
        <div data-display="" style={{ fontSize: 11.5, color: gold, marginBottom: 10 }}>DAILY HAND</div>
        <div style={{ fontSize: navMode ? 22 : 28, fontWeight: 700, color: C.text, lineHeight: 1.15, marginBottom: 10 }}>
          {t("Маленькая сессия.")}<br />{t("Большой прогресс.")}
        </div>
        <div style={{ fontSize: 13, color: C.mid, lineHeight: 1.5, marginBottom: 12 }}>
          {total
            ? <>{t("Твоя колода на сегодня уже на столе.")}<br />{t("Сыграй {0} {1} и закрой план.", [total, ruPlural(total, t("карточку"), t("карточки"), t("карточек"))])}</>
            : t("На сегодня всё сыграно. Новые карточки появятся завтра.")}
        </div>
        {total > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: C.faint }}>
            <span aria-hidden="true" style={{ width: 7, height: 7, background: C.dangerFill || C.danger }} />
            {t("{0} знакомых + {1} новых", [plan.repeat, plan.fresh])}
          </div>
        )}
      </div>
      {hand.length > 0 && (
        // веер повёрнутых карт — картинка (рамки повёрнутого текста выходят за карту): cover для автопроверки
        <div aria-hidden="true" data-layout-allow="cover" style={{ flex: '0 0 auto', position: 'relative', margin: navMode ? '4px auto 8px' : '0 12px 0 auto', padding: '14px 18px' }}>
          <div style={{ position: 'absolute', inset: 0, border: `1.5px dashed ${C.border}`, borderRadius: 10 }} />
          <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
            {hand.map((c, i) => <HandCard key={c.id || i} C={C} card={c} i={i} n={hand.length} k={navMode ? 0.78 : 1} />)}
          </div>
          {total > 0 && (
            <span data-display="" style={{ position: 'absolute', top: -12, right: -12, width: 34, height: 34, borderRadius: '50%', background: gold,
              color: '#14262a', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, boxShadow: '0 0 0 3px rgba(0,0,0,.25)' }}>{total}</span>
          )}
        </div>
      )}
    </div>
  );

  const cont = last && (
    <div data-panel="" style={{ ...panel, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
      <button type="button" onClick={() => onOpen(last.p)} aria-label={t("Открыть плеер")} className="ac-ibtn"
        style={{ width: 52, height: 52, flexShrink: 0, borderRadius: 6, background: C.face.bg, border: `1px solid ${C.face.border}`, color: C.face.text,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}>
        <span data-serif="" style={{ fontSize: 26, lineHeight: 1 }}>{[...(last.p.name || '?')][0]}</span>
      </button>
      <div role="button" tabIndex={0} onClick={() => onOpen(last.p)} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(last.p); }}
        style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.08em', color: C.faint, textTransform: 'uppercase' }}>{t("Продолжить")}</div>
        <div style={{ fontSize: 15.5, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {last.p.name} · {last.p.cardCount || 0} {ruPlural(last.p.cardCount || 0, t("карточка"), t("карточки"), t("карточек"))}
        </div>
        {last.pos > 0 && <div style={{ fontSize: 12.5, color: C.faint }}>{t("Остановились на {0}", [fmtClock(last.pos)])}</div>}
      </div>
      <IconButton C={C} variant="filled" size={40} label={playingId === last.p.id ? t("Пауза") : t("Играть здесь")}
        onClick={() => (onPlayHere ? onPlayHere(last.p) : onOpen(last.p))}>
        <Icon n={playingId === last.p.id ? 'pause' : 'play'} s={16} />
      </IconButton>
    </div>
  );

  const lib = (
    <section aria-label={t("Библиотека")}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text }}>{t("Библиотека")}</h2>
        <Button C={C} variant="primary" icon="plus" onClick={onNewMenu}>{t("Новый")}</Button>
      </div>
      {chips && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>{chips}</div>}
      {library}
    </section>
  );

  const slots = slotWords?.length > 0 && (
    <div>
      <WordSlots C={C} words={slotWords} variant="deck" />
      <div style={{ fontSize: 11.5, color: C.faint, lineHeight: 1.45, marginTop: 10 }}>
        {t("Немного удачи между занятиями.")}<br />{t("Главная ставка — твои знания.")}
      </div>
    </div>
  );

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', paddingTop: 8, fontSize: 11.5, color: C.faint }}>
      <span data-display="" style={{ color: C.mid }}>ANTONCARDS</span>
      <span>{t("Учись. Повторяй. Собирай свою колоду.")}</span>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: navMode ? 14 : 18, fontFamily: SANS }}>
      {navMode && <DeckWidgets C={C} cards={cards} onStart={() => onQuiz(null, true)} compact />}
      {hero}
      {cont}
      {navMode ? (
        <>{lib}{slots}</>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 170px', gap: 18, alignItems: 'start' }}>
          {lib}
          {slots ? <div style={{ marginTop: 46 }}>{slots}</div> : <span />}
        </div>
      )}
      {footer}
    </div>
  );
}
